<template>
  <div v-if="expenses != null">
    
    <div class="flex items-center">
      <div class="text-lg mr-4">
        Living Expenses - {{ getApplicantName(getClientPrimaryApplicant) }}
      </div>

      <NoteIcon page="living expenses" />
    </div>

    <div class="my-8">

      <div class="p-4 bg-gray-200 overflow-hidden text-sm">
        <div class="float-left" style="margin-top:3px;">
          <img src="https://cdn2.iconfinder.com/data/icons/officeicons/PNG/48/Help.png" class="w-4" />
        </div>

        <div style="margin:-1px 0 0 28px;" class="leading-normal">
          Note: The bank uses your expenses to assess your ability to service your loan, so only enter basic expenses. <br/>
          (Do not include liability and rent amounts that you entered previously)
        </div>
      </div>

      <div class="flex flex-wrap mt-8 border-b pb-2">
        <div class="w-11/12 pr-2">
          <div class="flex flex-wrap -mx-2">
            <div class="w-2/5 px-2">
              <div class="font-semibold text-sm">Expense</div>
            </div>

            <div class="w-1/5 px-2">
              <div class="font-semibold text-sm">Amount</div>
            </div>

            <div class="w-1/5 px-2 flex items-center">
              <div class="font-semibold text-sm mr-3">
                Frequency
              </div>

              <div class="" v-tooltip.top="{ html: 'frequencyTooltip' }">
                <img src="/static/img/icons/icons8-chevron_down_filled.png" class="w-4" />

                <div id="frequencyTooltip" class="p-1">
                  <div class="uppercase font-semibold mb-2 pb-2 border-b border-gray-700">Set frequency for all expenses</div>
                  <a href="#" class="mt-1 text-sm block text-white no-underline hover:underline" @click.prevent="setFrequency('weekly')">Weekly</a>
                  <a href="#" class="mt-1 text-sm block text-white no-underline hover:underline" @click.prevent="setFrequency('fortnightly')">Fortnightly</a>
                  <a href="#" class="mt-1 text-sm block text-white no-underline hover:underline" @click.prevent="setFrequency('monthly')">Monthly</a>
                  <a href="#" class="mt-1 text-sm block text-white no-underline hover:underline" @click.prevent="setFrequency('quarterly')">Quarterly</a>
                  <a href="#" class="mt-1 text-sm block text-white no-underline hover:underline" @click.prevent="setFrequency('yearly')">Annual</a>
                </div>
              </div>
            </div>

            <div class="w-1/5 px-2">
              <div class="font-semibold text-sm text-center">Monthly</div>
            </div>
          </div>
        </div>

        <div class="w-1/12">
          &nbsp;
        </div>
      </div>

      <draggable v-model="expenses" @end="expenseMoved" :options="{handle:'.move-handler'}">
        <template v-for="(expense, index) in expenses">
          <div class="flex flex-wrap mt-3">
            <div class="w-11/12 pr-2">
              <div class="flex flex-wrap -mx-2">
                <div class="w-2/5 px-2">
                  <InputText 
                    propertyKey="name"
                    objectType="expense"
                    :index="index"
                    :value="expense.name"
                    :updateValue="updateValue" />
                </div>

                <div class="w-1/5 px-2">
                  <InputDollarValue 
                    propertyKey="amount"
                    objectType="expense"
                    :index="index"
                    :value="expense.amount"
                    :updateValue="updateValue"/>
                </div>

                <div class="w-1/5 px-2">
                  <select class="select-standard"
                    :value="expense.frequency"
                    v-on:change="updateValue({
                      key  : 'frequency', 
                      value: $event.target.value,
                      index: index
                    })">
                    <option value="-1" disabled="">Select an option...</option>
                    <option value="weekly">Weekly</option>
                    <option value="fortnightly">Fortnightly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="annually">Annually</option>
                  </select>
                </div>

                <div class="w-1/5">
                  <div class="text-right text-base text-gray-900 bg-gray-200 ml-2 px-2 rounded"
                    style="line-height:44px;">{{ getMonthlyValue(expense.amount, expense.frequency) }}</div>
                </div>
              </div>
            </div>

            <div class="w-1/12 text-right mt-2">
              <template v-if="$store.state.view == 'broker'">
                <a href="#" @click.prevent
                  class="move-handler lg:mr-2 text-gray-600 hover:text-blue-600"
                  v-tooltip.top="{ content : 'Click &amp; drag to move' }">
                  <svg width="16" height="16" stroke-width="2" class="inline" stroke="currentColor" fill="none">
                    <use xlink:href="/static/img/feather-sprite.svg#move"/>
                  </svg>                      
                </a>

                <a href="#" 
                  @click.prevent="deleteExpense(expense.id, index)" 
                  class="text-gray-500 hover:text-red-500">
                  <svg width="18" height="18" stroke-width="2" class="inline" stroke="currentColor" fill="none">
                    <use xlink:href="/static/img/feather-sprite.svg#x-circle"/>
                  </svg>
                </a>
              </template>
            </div>
          </div>
        </template>
      </draggable>

      <div class="flex flex-wrap mt-4 pt-4 border-t-2 border-black">
        <div class="w-11/12 pr-2">
          <div class="flex flex-wrap -mx-2">
            <div class="w-2/5 px-2">
              <a href="#" 
                @click.prevent="add()" 
                class="text-white bg-green-500 font-semibold inline-block text-xs no-underline hover:bg-green-600 p-2">Add an expense</a>

              <a href="#" 
                v-if="$store.state.view == 'broker'"
                @click.prevent="copy()" 
                class="text-white bg-orange-500 font-semibold inline-block text-xs no-underline hover:bg-orange-dark p-2 ml-2">Save for next time</a>

              <a
                href="#"
                v-if="$store.state.view == 'broker'"
                @click.prevent="exportExcel()"
                class="text-white bg-blue-500 font-semibold inline-block text-xs no-underline hover:bg-blue-dark p-2 ml-2"
                >Export to Excel</a
              >

              <a href="#" 
                v-if="$store.state.view == 'broker'"
                @click.prevent="reset()" 
                class="text-white bg-gray-600 font-semibold inline-block text-xs no-underline hover:bg-gray-600 p-2 ml-2">Reset</a>
            </div>

            <div class="w-1/5 px-2">
              &nbsp;
            </div>

            <div class="w-1/5 px-2">
              <div class="font-semibold text-base text-right mt-2">Monthly Total</div>
            </div>

            <div class="w-1/5 pl-2">
              <div class="text-right text-base font-semibold bg-gray-200 px-2 rounded"
                style="line-height:38px;">{{ getTotalExpenseValue }}</div>
            </div>
          </div>
        </div>

        <div class="w-1/12">
          &nbsp;
        </div>
      </div>

      <!-- <div class="mt-10 p-4 bg-gray-100 border">
        <PageComment page='expenses'></PageComment>
      </div> -->

      <div class="button-line">
        <ApplicationButton />
      </div>
    </div>
  </div>
</template>

<script>
  import { EventBus }       from '@/shared/event-bus'
  import draggable          from 'vuedraggable'
  import commaNumber        from 'comma-number'
  import InputText          from '@/shared/components/form/InputText'
  import InputDollarValue   from '@/shared/components/form/InputDollarValue'
  import ApplicationButton  from '@/shared/components/form/ApplicationButton'
  import NoteIcon           from '@/shared/components/NoteIcon'
  import axios from "axios";

  export default {

    components: {
      draggable,
      InputText,
      InputDollarValue,
      NoteIcon,
      ApplicationButton
    },

    data() {
      return {
        expenses: null
      }
    },

    created() {
      this.logActivity({
        type        : 'expense-visited',
        clientId    : this.$route.params.clientId,
        currentView : this.$store.state.view
      })

      this.getExpenses()
    },

    computed : {
      getTotalExpenseValue() {
        let total = 0
        for(let i=0; i < this.expenses.length; i++) {
          if(this.expenses[i].amount != null && this.expenses[i].amount.toString().length > 0) {
            let value = parseFloat(this.expenses[i].amount.toString().replace(/,/g, ""))

            if(this.expenses[i].frequency == 'weekly') {
              value = (value * 52) / 12
            }

            else if(this.expenses[i].frequency == 'fortnightly') {
              value = (value * 26) / 12
            }

            else if(this.expenses[i].frequency == 'quarterly') {
              value = (value * 4) / 12
            }

            else if(this.expenses[i].frequency == 'annually') {
              value = value / 12  
            }

            total += value
          }
        }

        return "$" + commaNumber(this.roundTo2(total))
      },
    },

    methods : {

      getMonthlyValue(value, frequency) {
        if(value != null && value.toString().length > 0) {

          value = parseFloat(value.toString().replace(/,/g, ""))
          
          if(frequency == 'weekly') {
            value = (value * 52) / 12
          }

          else if(frequency == 'fortnightly') {
            value = (value * 26) / 12
          }

          // else if(frequency == 'monthly') {

          // }

          else if(frequency == 'quarterly') {
            value = (value * 4) / 12
          }

          else if(frequency == 'annually') {
            value = value / 12  
          }

          value = '$' + this.commaNumber(Math.round(value))
        }

        else {
          value = '$0'
        }

        return value        
      },

      getExpenses() {
        this.showLoader("Loading expenses...")
        this.apiGet({
          clientId : this.$route.params.clientId
        }, 'expense/list', (expenses) => {
          this.hideLoader()
          this.expenses = expenses
        })
      },

      setFrequency(value) {
        for(let i=0; i < this.expenses.length; i++) {
          this.expenses[i].frequency = value
        }

        this.saveAll()
      },

      add() {
        this.showLoader("Adding an expense...")
        this.apiPost({
          clientId : this.$route.params.clientId,
        }, 'expense/create', (expense) => {
          this.hideLoader()
          this.expenses.push(expense)
          window.scrollTo(0, document.body.scrollHeight || document.documentElement.scrollHeight)          
        })
      },

      deleteExpense(expenseId, index) {
        this.$modal.show('confirm-modal', {
          // width : 600,
          title  : "Please confirm your action",
          message: "Are you sure you want to delete this?",
          buttons:[{
            label          : 'Yes',
            color          : 'red',
            loadingViaLocal: true, // a bit of HACK, we want to show loading image when button is clicked, but hard to pass loading prop to a modal
            action         : () => {
              this.apiDelete({
                clientId : this.$route.params.clientId,
                expenseId: expenseId
              }, 'expense/delete', () => {
                this.$modal.hide('confirm-modal')
                this.expenses.splice(index, 1)   
                EventBus.$emit('notification-event', {
                  'type'   : 'success',
                  'message': "Expense deleted successfully"
                });
              })
            }
          }, {
            label : 'No',
            color : 'gray',
            action: () => {
              this.$modal.hide('confirm-modal')
            }
          }]
        })
      },

      updateValue(params) {
        if(this.expenses[params.index][params.key] != params.value) {
          EventBus.$emit('set-autosave', true)
          this.expenses[params.index][params.key] = params.value
          this.apiPost({
            clientId : this.$route.params.clientId,
            expenseId: this.expenses[params.index].id,
            key      : params.key,
            value    : params.value            
          }, 'expense/edit', () => {
            EventBus.$emit('set-autosave', false)
          })
        }
      },

      // a method that will save all the expenses
      saveAll() {
        EventBus.$emit('set-autosave', true)
        this.apiPost({
          clientId : this.$route.params.clientId,
          expenses : this.expenses          
        }, 'expense/edit-all', () => {
          EventBus.$emit('set-autosave', false)
          EventBus.$emit('notification-event', {
            'type'   : 'success', 
            'message': 'Changes saved successfully'
          })
        })
      },

      expenseMoved(event) {
        EventBus.$emit('set-autosave', true)

        let ids = []
        for(let i=0; i < this.expenses.length; i++) {
          ids.push(this.expenses[i].id)
        }

        this.apiPost({
          clientId  : this.$route.params.clientId,
          expenseIds: ids.join(",")          
        }, 'expense/change-order', () => {
          EventBus.$emit('set-autosave', false)
        })
      },

      copy() {
        let r = confirm("Are you sure you would like to save the monthly expenses for use with other clients?");
        if(r) {
          this.showLoader("Copying...")
          this.apiPost({
            clientId : this.$route.params.clientId
          }, 'expense/copy', () => {
            this.hideLoader()
            EventBus.$emit('notification-event', {
              'type'   : 'success', 
              'message': 'Expenses saved.'
            });
          })
        }
      },


      exportExcel() {
        this.showLoader("Exporting...");
        axios
          .get(
            process.env.VUE_APP_API_URL +
              "/api/expense/export-excel?clientId=" +
              this.$route.params.clientId,
            {
              responseType: "blob",
              headers: {
                Authorization:
                  "Bearer " + localStorage.getItem("bp-broker-jwt"),
              },
            }
          )
          .then((response) => {
            this.hideLoader();
            const applicantName = this.getApplicantName(
              this.getClientPrimaryApplicant
            );
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
              "download",
              applicantName + "_Living_Expenses.xlsx"
            ); //or any other extension
            document.body.appendChild(link);
            link.click();

            EventBus.$emit("notification-event", {
              type: "success",
              message: "Excel is successfully exported!.",
            });
          })
          .catch((e) => {
            this.hideLoader();
          })
      },

      reset() {
        let r = confirm("Are you sure you would like to reset the calculator?");
        if(r) {
          this.showLoader("Resetting...")
          this.apiPost({
            clientId : this.$route.params.clientId
          }, 'expense/reset', () => {
            this.getExpenses()
            this.hideLoader()
            EventBus.$emit('notification-event', {
              'type'   : 'success', 
              'message': 'Expenses reset.'
            });
          })
        }
      },
    },

    metaInfo () {
      return {
        title: 'Living Expenses - ' + (this.getClientPrimaryApplicant != null ? this.getApplicantName(this.getClientPrimaryApplicant) : '') + " | Brokerpad"
      }
    }
  }
</script>